// React
import { useState } from 'react';

// Mantine
import {
  Button,
  Flex,
  Modal,
  Stack,
  TextInput,
  Title,
  useComputedColorScheme,
  Checkbox,
} from '@mantine/core';

// API
import {
  addProviderToSite,
  providerQueryKeys,
  useUpdateProvider,
} from 'api/resource';

// Interfaces
import { Payor, Provider, Site, User } from 'interfaces';

// Utils
import { buttonTextColor } from 'utils/buttonUtils';

// Sentry
import * as Sentry from '@sentry/react';
import { IconX } from '@tabler/icons-react';
import { AddProviderToSiteSearch } from './AddProviderToSiteSearch';
import { MRT_TableInstance } from 'mantine-react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useCurrentSiteStore } from 'store/SiteStore';
import { ProviderLicensesComponent } from './ProviderLicensesComponent';

interface AddProviderModalProps {
  addProviderModal: boolean;
  setAddProviderModal: (value: boolean) => void;
  siteProviders: Provider[];
  currentSite: Site;
  payors: Payor[];
  table: MRT_TableInstance<Provider>;
  handleCreateUser: (
    provider: Provider,
    email?: string
  ) => Promise<User | null>;
}

export const AddProviderModal: React.FC<AddProviderModalProps> = ({
  addProviderModal,
  setAddProviderModal,
  handleCreateUser,
  siteProviders,
  currentSite,
  table,
  payors,
}) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const { id: currentSiteID } = useCurrentSiteStore();
  const updateProvider = useUpdateProvider();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );
  const [providerEmailError, setProviderEmailError] = useState<string | null>(
    null
  );
  const [createUserForExisting, setCreateUserForExisting] =
    useState<boolean>(false);
  const [hasValidBillingNumbers, setHasValidBillingNumbers] =
    useState<boolean>(true);

  const handleSubmit = async (provider: Provider) => {
    if (createUserForExisting && !provider.email) {
      setProviderEmailError('Provider email is required.');
      return;
    }

    const providerAlreadyInCurrentSite = siteProviders.some(
      (siteProvider) => siteProvider.id === provider.id
    );

    setLoading(true);
    try {
      if (!providerAlreadyInCurrentSite) {
        await addProviderToSite({
          providerID: provider.id,
          siteID: currentSite.id,
        });
      }
      await updateProvider.mutateAsync({
        ...provider,
        siteID: currentSite.id,
      });
      if (createUserForExisting) {
        const createdUser = await handleCreateUser(
          provider,
          provider.email ?? undefined
        );
        if (!createdUser) {
          return;
        }
        provider.userID = createdUser.id;
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoading(false);
      setSelectedProvider(null);
      setProviderEmailError(null);
      queryClient.invalidateQueries({
        queryKey: providerQueryKeys.listBySite(currentSite.id),
      });
      setAddProviderModal(false);
    }
  };

  const handleCloseModal = () => {
    setAddProviderModal(false);
    setSelectedProvider(null);
    setCreateUserForExisting(false);
    setProviderEmailError(null);
    setHasValidBillingNumbers(true);
  };

  const handleProviderEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!selectedProvider) return;
    const email = event.currentTarget.value;
    setProviderEmailError(null);
    setSelectedProvider({ ...selectedProvider, email });
  };

  const selectedProviderAlreadyInCurrentSite =
    !!selectedProvider &&
    siteProviders.some(
      (siteProvider) => siteProvider.id === selectedProvider.id
    );

  return (
    <Modal opened={addProviderModal} onClose={handleCloseModal} size="lg">
      <Stack style={{ marginBottom: '5%' }} data-testid="add-provider-modal">
        <Title order={3}>Manage Providers</Title>
        <AddProviderToSiteSearch
          siteProviders={siteProviders}
          selectedProviders={selectedProvider}
          setSelectedProviders={(arr) => {
            setSelectedProvider(arr || null);
            setCreateUserForExisting(false);
            setProviderEmailError(null);
          }}
          onAddNewProvider={() => {
            // close this modal and trigger create new provider flow
            setAddProviderModal(false);
            table.setCreatingRow(true);
          }}
        />
        {selectedProvider && (
          <Flex
            key={selectedProvider.id}
            justify={'space-between'}
            align={'center'}
            direction={'column'}
            w={'100%'}
            gap={'xl'}
            h={'100%'}
          >
            {isAdmin ? (
              <>
                <TextInput
                  w={'100%'}
                  id={`provider-${selectedProvider.id}`}
                  value={selectedProvider?.email || ''}
                  disabled={!createUserForExisting}
                  label={`${selectedProvider?.firstName} ${selectedProvider?.lastName}`}
                  onChange={handleProviderEmailChange}
                  error={providerEmailError}
                  rightSection={
                    <IconX
                      onClick={() => {
                        setSelectedProvider(null);
                      }}
                    />
                  }
                  placeholder={
                    createUserForExisting
                      ? 'Enter Provider Email'
                      : 'Email (toggle create portal user to edit)'
                  }
                  data-testid="providerEmail"
                />
                {!selectedProvider.userID && !selectedProvider.user && (
                  <Checkbox
                    mt="sm"
                    label="Create Portal User Account"
                    checked={createUserForExisting}
                    onChange={(e) =>
                      setCreateUserForExisting(e.currentTarget.checked)
                    }
                    data-testid="create-user-checkbox"
                  />
                )}
                <ProviderLicensesComponent
                  payors={payors}
                  siteID={currentSiteID}
                  licenses={selectedProvider?.licenses}
                  onChange={(newLicenses) => {
                    const normalized = newLicenses.map((l) => ({
                      ...l,
                      payor: payors.find((p) => p.id === l.payorID),
                    }));
                    setSelectedProvider((prev) =>
                      prev ? { ...prev, licenses: normalized } : null
                    );
                  }}
                  onValidationChange={setHasValidBillingNumbers}
                />
              </>
            ) : (
              <>
                {!selectedProvider.userID && !selectedProvider.user && (
                  <>
                    <TextInput
                      w={'100%'}
                      id={`provider-${selectedProvider.id}`}
                      value={selectedProvider?.email}
                      label={'Email'}
                      onChange={handleProviderEmailChange}
                      error={providerEmailError}
                      disabled={!createUserForExisting}
                      placeholder={`Enter ${selectedProvider.title} ${selectedProvider.lastName}'s email`}
                      data-testid="providerEmail"
                    />
                    <Checkbox
                      mt="sm"
                      label="Create Portal User Account"
                      checked={createUserForExisting}
                      onChange={(e) =>
                        setCreateUserForExisting(e.currentTarget.checked)
                      }
                      data-testid="create-user-checkbox"
                    />
                  </>
                )}
                <ProviderLicensesComponent
                  payors={payors}
                  siteID={currentSiteID}
                  licenses={selectedProvider?.licenses}
                  onChange={(newLicenses) => {
                    const normalized = newLicenses.map((l) => ({
                      ...l,
                      payor: payors.find((p) => p.id === l.payorID),
                    }));
                    setSelectedProvider((prev) =>
                      prev ? { ...prev, licenses: normalized } : null
                    );
                  }}
                  onValidationChange={setHasValidBillingNumbers}
                />
              </>
            )}
          </Flex>
        )}
      </Stack>
      <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
        <Button
          loading={loading}
          type="submit"
          variant="outline"
          radius="md"
          color="green"
          disabled={!selectedProvider || loading || !hasValidBillingNumbers}
          c={buttonTextColor(computedColorScheme, loading)}
          onClick={async () => {
            await handleSubmit(selectedProvider);
          }}
          data-testid="submit-existing-providers"
        >
          {createUserForExisting
            ? selectedProviderAlreadyInCurrentSite
              ? 'Create Portal User'
              : 'Create Portal User and Add to Site'
            : selectedProviderAlreadyInCurrentSite
            ? 'Save Provider'
            : 'Add to Site'}
        </Button>
      </Flex>
    </Modal>
  );
};
